import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ReportBaseComponent } from '../report-base.component';
import { TestResult_PercentPositiveByRunDateRow } from '../../../services/search.service.testresult.models';
import { MetadataService } from '../../../services/metadata.service';
import { TestType, InitDataSelectionLists } from '../../../services/metadata.service.models';
import { BusyService } from '../../../services/busy.service';
import { ChartType } from '../../charts/google-charts.types';

@Component({
  selector: 'core-percent-positive-run-date',
  templateUrl: './percent-positive-run-date.component.html',
  styleUrls: ['./percent-positive-run-date.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PercentPositiveRunDateComponent extends ReportBaseComponent implements OnInit, OnDestroy {

  @Input() data: TestResult_PercentPositiveByRunDateRow[];
  showChart: boolean;
  chartType: ChartType;
  chartTitle: string;
  chartData: Array<Array<string | number>>;
  chartOptions: Record<string, unknown>;
  chartRoles: Array<{ type: string; role: string; index?: number }>;
  chartColumnNames: Array<string>;
  testTypes: TestType[];

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    elementRef: ElementRef<HTMLElement>,
    busyService: BusyService,
    private metadataService: MetadataService) {
    super(changeDetectorRef, translateService, 'PercentPositiveByRunDate', busyService, elementRef);
    this.showChart = false;
  }

  ngOnInit(): void {
    this.subscription.add(this.metadataService.fetchInitDataSelectionLists().subscribe((initData: InitDataSelectionLists) => {
      this.testTypes = initData.TestTypes;

      if (this.data) {
        this.setData(this.data);
      }
    }));

    this.chartType = ChartType.LineChart;
    this.chartOptions = {
      legend: 'right',
      bar: { groupWidth: '75%' },
      isStacked: true,
      interpolateNulls: true,
      hAxis: {
        title: this.getText('HAxis'),
        slantedText: true,
        slantedTextAngle: 90,
        showTextEvery: 1,
      },
      vAxis: {
        title: this.getText('VAxis')
      },
      chartArea: { left: 150, right: 250, top: 40, bottom: 200 }
    };
    this.chartData = [[]];
  }

  ngOnDestroyInternal(): void {
    // Required by base component
  }

  onTranslateResourcesLoaded(): void {
    this.setTitle();
  }

  onTranslateLanguageChanged(): void {
    this.setTitle();
  }

  private setTitle() {
    this.chartTitle = this.getText('Title');
  }

  public setData(data: TestResult_PercentPositiveByRunDateRow[]): void {
    this.beginSetData();
    this.data = data;

    if (!this.testTypes) {
      return;
    }

    const columnNames = ['DataGroup'];
    const dateGroups: string[] = [];
    const convData: (string | number)[][] = [];
    const series: Record<string, unknown>[] = [];

    data.forEach((row) => {
      let dateGroupNdx = dateGroups.indexOf(row.dateGroup);
      if (dateGroupNdx < 0) {
        dateGroups.push(row.dateGroup);
        dateGroupNdx = dateGroups.length - 1;
        convData.push([row.dateGroup]);
      }

      let columnNameNdx = columnNames.indexOf(row.testName);
      if (columnNameNdx < 0) {
        columnNames.push(row.testName);
        columnNameNdx = columnNames.length - 1;

        const test = this.testTypes.find(t => t.testName === row.testName);
        if (test) {
          series.push({
            color: test ? test.color : '#98999b',
            pointSize: 15,
            pointShape: {
              type: test && test.symbol ? test.symbol : '',
              rotation: test.symbolRotation ? test.symbolRotation : 0
            }
          });
        }
      }

      const dataRow = convData[dateGroupNdx];
      while (dataRow.length < columnNameNdx - 1) {
        dataRow.push(0);
      }
      dataRow[columnNameNdx] = row.percentPositive;
      convData[dateGroupNdx] = dataRow;
    });

    // Fill in holes
    convData.forEach((dataRow) => {
      while (dataRow.length < columnNames.length) {
        dataRow.push(0);
      }
    });

    this.chartColumnNames = columnNames;
    this.chartData = convData;
    this.chartOptions.series = series;

    this.showChart = true;
  }
}
